import * as React from "react";
import { AlertTriangle, Copy, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/auth-context";
import { getElternTemplate } from "@/lib/notification-templates";
import type { LevelRiseEventDetail } from "./auto-toast-on-level-change";

/**
 * AutoLevelToast — bottom-center-toast bei stufen-erhöhung.
 *
 * Listener für `hitzeprotokoll:level-rise`-event (siehe useAutoToastOnLevelChange).
 * Auto-dismiss 30s, manuell via X-button. Kopier-action ist defensiv: wenn
 * getElternTemplate null returned (stufe 1 oder unbekannte stufe), wird der
 * button disabled mit aria-label-hint.
 */
const AUTO_DISMISS_MS = 30_000;

// B3: DB-Werte 0-3 (kein 1-4-Offset)
const LEVEL_LABEL: Record<number, string> = {
  1: "Soll-Maßnahmen empfohlen",
  2: "Muss-Maßnahmen erforderlich",
  3: "Kritisch — sofort handeln",
};

export function AutoLevelToast(): React.ReactElement | null {
  const [event, setEvent] = React.useState<LevelRiseEventDetail | null>(null);
  const [copyState, setCopyState] = React.useState<"idle" | "ok" | "fail">("idle");
  const dismissTimerRef = React.useRef<number | null>(null);
  const auth = useAuth();
  // Fallback "Unsere Einrichtung" wenn auth-context noch nicht geladen
  // oder kein email-domain — konsistent mit ElternPushModal.
  const orgName = auth.user?.email?.split("@")[1] ?? "Unsere Einrichtung";

  const dismiss = React.useCallback(() => {
    setEvent(null);
    setCopyState("idle");
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<LevelRiseEventDetail>).detail;
      setEvent(detail);
      setCopyState("idle");
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
      }
      dismissTimerRef.current = window.setTimeout(() => dismiss(), AUTO_DISMISS_MS);
    };
    window.addEventListener("hitzeprotokoll:level-rise", handler);
    return () => {
      window.removeEventListener("hitzeprotokoll:level-rise", handler);
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
      }
    };
  }, [dismiss]);

  if (!event) return null;
  // B3: event.level ist DB-Wert 0-3. getElternTemplate erwartet 1-4 (altes schema).
  // Bridge: DB-Wert +1 für den template-lookup bis notification-templates auf 0-3 migriert.
  // Stufe 0 → keine Eltern-Info (keine template). Stufe 1-3 → +1 → template 2-4.
  const templateLevel = event.level > 0 ? ((event.level + 1) as 2 | 3 | 4) : null;
  const template = templateLevel ? getElternTemplate(templateLevel) : null;
  const labelText = LEVEL_LABEL[event.level] ?? "Eltern-Info empfohlen.";

  const handleCopy = async () => {
    if (!template) return;
    // {{org_name}}-placeholder ersetzen (konsistent mit ElternPushModal).
    // Default-templates haben aktuell kein placeholder, aber admin-overrides
    // können — ohne replace würde "{{org_name}}" literal kopiert werden.
    const title = template.title.replace(/\{\{org_name\}\}/g, orgName);
    const body = template.body.replace(/\{\{org_name\}\}/g, orgName);
    try {
      await navigator.clipboard.writeText(`${title}\n\n${body}`);
      setCopyState("ok");
    } catch {
      setCopyState("fail");
    }
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        "fixed bottom-4 left-1/2 z-[var(--z-toast)] -translate-x-1/2",
        "flex w-[calc(100%-2rem)] max-w-md flex-col gap-3",
        "rounded-lg border-l-4 border-l-[color:var(--color-warning)]",
        "bg-[color:var(--color-neutral-900)] p-4 text-white",
        "shadow-[var(--shadow-lg)]",
        "anim-slide-up-fade-in",
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          aria-hidden="true"
          strokeWidth={2}
          className="mt-0.5 size-5 shrink-0 text-[color:var(--color-warm-orange-300)]"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Stufe {event.level} erreicht</p>
          <p className="mt-0.5 text-sm text-neutral-300">{labelText}</p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hinweis schließen"
          className="-m-1 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-white"
        >
          <X aria-hidden="true" strokeWidth={2} className="size-4" />
        </button>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={dismiss}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-neutral-300 hover:bg-neutral-800"
        >
          Später
        </button>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!template}
          aria-label={!template ? "Eltern-Text wird ergänzt — noch nicht verfügbar" : undefined}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5",
            "text-xs font-semibold",
            "bg-[color:var(--color-brand-primary-strong)] text-white",
            "hover:bg-[color:var(--color-warm-orange-800)]",
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[color:var(--color-brand-primary-strong)]",
          )}
        >
          <Copy aria-hidden="true" strokeWidth={2.25} className="size-3.5" />
          {copyState === "ok" ? "Kopiert" : "Eltern-Text kopieren"}
        </button>
      </div>
      {copyState === "fail" && (
        <p className="text-xs text-[color:var(--color-warm-orange-300)]">
          Kopieren fehlgeschlagen. Bitte manuell aus Wiki entnehmen.
        </p>
      )}
    </div>
  );
}

AutoLevelToast.displayName = "AutoLevelToast";
